import { supabase } from '@/integrations/supabase/client';
import type { ExtractBrandInfoResult } from '../types/brand.types';
import type { BrandInfoExtractorPort } from '../ports/brandInfoExtractor.port';
import { isExtractBrandInfoResult } from '../validators/brand.validators';

const DEFAULT_ERROR = 'Could not fetch brand details. Please try again.';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const readFunctionErrorMessage = async (error: unknown): Promise<string> => {
  if (isRecord(error) && error.context instanceof Response) {
    try {
      const body: unknown = await error.context.clone().json();
      if (isRecord(body) && typeof body.error === 'string' && body.error) {
        return body.error;
      }
    } catch {
      // Body was not JSON
    }
  }

  if (error instanceof Error && error.message) return error.message;
  return DEFAULT_ERROR;
};

export class ApifyBrandInfoExtractor implements BrandInfoExtractorPort {
  async extractBrandInfo(orgId: string, input: string): Promise<ExtractBrandInfoResult> {
    const trimmedInput = input.trim();
    if (!trimmedInput) {
      throw new Error('Enter a brand name or website.');
    }

    const { data, error } = await supabase.functions.invoke<unknown>('extract-brand-info', {
      body: { input: trimmedInput, orgId },
    });

    if (error) {
      throw new Error(await readFunctionErrorMessage(error));
    }

    if (!isExtractBrandInfoResult(data)) {
      throw new Error('Received an unexpected response while fetching brand details.');
    }

    return data;
  }
}

export const apifyBrandInfoExtractor = new ApifyBrandInfoExtractor();
